import cloneDeep from "lodash/cloneDeep";
import { isResponsiveValue } from "@circle-react-shared/BuilderUI/Preview/isResponsiveValue";
import type { ResponsiveValue } from "@circle-react-shared/BuilderUI/Preview/isResponsiveValue";
import type { BoxModelValues } from "@circle-react-shared/BuilderUI/Preview/isResponsiveValue";
import type { ScreenSize } from "@circle-react-shared/ScreenSizeSelect";
import { areRawValuesEqual } from "./areRawValuesEqual";
import { isRecord, isStyleValue } from "./typePredicates";

const isStylePropertyValue = (
  value: unknown,
): value is string | number | BoxModelValues =>
  typeof value === "string" || typeof value === "number" || isRecord(value);

const SIZE_HIERARCHY: ScreenSize[] = ["desktop", "tablet", "mobile"];

const applyCascadingUpdates = (
  responsiveValue: ResponsiveValue,
  canvasSize: ScreenSize,
  newValue: string | number | BoxModelValues,
  oldValue: unknown,
  cssVariables: Record<string, string>,
): ResponsiveValue => {
  const currentSizeIndex = SIZE_HIERARCHY.indexOf(canvasSize);

  responsiveValue[canvasSize] = newValue;

  for (let i = currentSizeIndex + 1; i < SIZE_HIERARCHY.length; i++) {
    const smallerSize = SIZE_HIERARCHY[i];
    if (
      areRawValuesEqual(responsiveValue[smallerSize], oldValue, cssVariables)
    ) {
      responsiveValue[smallerSize] = newValue;
    }
  }

  return responsiveValue;
};

const updateResponsiveValue = (
  existingValue: ResponsiveValue,
  canvasSize: ScreenSize,
  newValue: unknown,
  cssVariables: Record<string, string>,
): ResponsiveValue => {
  const currentCanvasValue = existingValue[canvasSize];

  if (areRawValuesEqual(currentCanvasValue, newValue, cssVariables)) {
    return existingValue;
  }

  if (!isStylePropertyValue(newValue)) {
    return {
      desktop: currentCanvasValue,
      mobile: currentCanvasValue,
      tablet: currentCanvasValue,
    };
  }

  return applyCascadingUpdates(
    { ...existingValue },
    canvasSize,
    newValue,
    currentCanvasValue,
    cssVariables,
  );
};

const createResponsiveValue = (
  existingValue: unknown,
  newValue: unknown,
  canvasSize: ScreenSize,
  cssVariables: Record<string, string>,
): ResponsiveValue => {
  if (!isStylePropertyValue(existingValue)) {
    return { desktop: "", mobile: "", tablet: "" };
  }

  if (areRawValuesEqual(existingValue, newValue, cssVariables)) {
    const typedValue = existingValue;
    return { desktop: typedValue, mobile: typedValue, tablet: typedValue };
  }

  const responsiveValue: ResponsiveValue = {
    desktop: existingValue,
    mobile: existingValue,
    tablet: existingValue,
  };

  if (!isStylePropertyValue(newValue)) {
    return {
      desktop: existingValue,
      mobile: existingValue,
      tablet: existingValue,
    };
  }

  return applyCascadingUpdates(
    responsiveValue,
    canvasSize,
    newValue,
    existingValue,
    cssVariables,
  );
};

export const mergeNewStylesWithCanvasSize = (
  styles: Record<string, unknown>,
  newStyles: unknown,
  cssVariables: Record<string, string>,
  canvasSize: ScreenSize,
): Record<string, unknown> => {
  if (!isRecord(newStyles)) return styles;

  const merged = cloneDeep(styles);

  for (const [key, value] of Object.entries(newStyles)) {
    const existingValue = styles[key];

    if (isStyleValue(existingValue)) {
      const existingValueObj = existingValue.value;
      const hasCanvasSizeStructure = isResponsiveValue(existingValueObj);

      if (hasCanvasSizeStructure) {
        const updatedValue = updateResponsiveValue(
          existingValueObj,
          canvasSize,
          value,
          cssVariables,
        );
        merged[key] = { ...existingValue, value: updatedValue };
      } else {
        const canvasSizeValue = createResponsiveValue(
          existingValueObj,
          value,
          canvasSize,
          cssVariables,
        );
        merged[key] = { ...existingValue, value: canvasSizeValue };
      }
    } else if (isRecord(value) && isRecord(existingValue)) {
      merged[key] = mergeNewStylesWithCanvasSize(
        existingValue,
        value,
        cssVariables,
        canvasSize,
      );
    } else {
      merged[key] = existingValue;
    }
  }

  return merged;
};
